var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

/*
 * This file is part of the nivo project.
 *
 * Copyright 2016-present, Raphaël Benitte.
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { memo, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Measure from 'react-measure';
import { Motion, spring } from 'react-motion';
import { useTheme, useMotionConfig } from '@nivo/core';

var TOOLTIP_OFFSET = 14;

var tooltipStyle = {
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: 10,
    top: 0,
    left: 0
};

var TooltipWrapper = memo(function (_ref) {
    var position = _ref.position,
        anchor = _ref.anchor,
        children = _ref.children;

    var _useState = useState(null),
        dimensions = _useState[0],
        setDimensions = _useState[1];

    var theme = useTheme();

    var _useMotionConfig = useMotionConfig(),
        animate = _useMotionConfig.animate,
        springConfig = _useMotionConfig.springConfig;

    var x = Math.round(position[0]);
    var y = Math.round(position[1]);
    if (dimensions !== null) {
        if (anchor === 'top') {
            x -= dimensions[0] / 2;
            y -= dimensions[1] + TOOLTIP_OFFSET;
        } else if (anchor === 'right') {
            x += TOOLTIP_OFFSET;
            y -= dimensions[1] / 2;
        } else if (anchor === 'bottom') {
            x -= dimensions[0] / 2;
            y += TOOLTIP_OFFSET;
        } else if (anchor === 'left') {
            x -= dimensions[0] + TOOLTIP_OFFSET;
            y -= dimensions[1] / 2;
        } else if (anchor === 'center') {
            x -= dimensions[0] / 2;
            y -= dimensions[1] / 2;
        }
    }

    var style = useMemo(function () {
        return _extends({}, tooltipStyle, theme.tooltip, {
            transform: 'translate(' + x + 'px, ' + y + 'px)',
            opacity: dimensions === null ? 0 : 1
        });
    }, [x, y, dimensions, theme.tooltip]);

    // if we don't have dimensions yet, we use
    // the non animated version with a 0 opacity
    // to avoid a flash effect and weird initial transition
    if (animate !== true || dimensions === null) {
        return React.createElement(
            Measure,
            {
                client: false,
                offset: false,
                bounds: true,
                margin: false,
                onResize: function onResize(_ref2) {
                    var bounds = _ref2.bounds;

                    setDimensions([bounds.width, bounds.height]);
                }
            },
            function (_ref3) {
                var measureRef = _ref3.measureRef;
                return React.createElement(
                    'div',
                    { ref: measureRef, style: style },
                    children
                );
            }
        );
    }

    return React.createElement(
        Motion,
        {
            style: {
                x: spring(x, springConfig),
                y: spring(y, springConfig)
            }
        },
        function (animatedPosition) {
            return React.createElement(
                Measure,
                {
                    client: false,
                    offset: false,
                    bounds: true,
                    margin: false,
                    onResize: function onResize(_ref4) {
                        var bounds = _ref4.bounds;

                        setDimensions([bounds.width, bounds.height]);
                    }
                },
                function (_ref5) {
                    var measureRef = _ref5.measureRef;
                    return React.createElement(
                        'div',
                        {
                            ref: measureRef,
                            style: _extends({}, tooltipStyle, theme.tooltip, {
                                transform: 'translate3d(' + animatedPosition.x + 'px, ' + animatedPosition.y + 'px, 0)'
                            })
                        },
                        children
                    );
                }
            );
        }
    );
});

TooltipWrapper.displayName = 'TooltipWrapper';
TooltipWrapper.propTypes = process.env.NODE_ENV !== "production" ? {
    position: PropTypes.array.isRequired,
    anchor: PropTypes.oneOf(['top', 'right', 'bottom', 'left', 'center']).isRequired,
    children: PropTypes.node.isRequired
} : {};
TooltipWrapper.defaultProps = {
    anchor: 'top'
};

export default TooltipWrapper;